import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
} from "@material-ui/core";
import LanguageIcon from "@material-ui/icons/Language";
import EditIcon from "@material-ui/icons/Edit";
import PasswordIcon from "@material-ui/icons/VpnKey";
import CircleIcon from "@material-ui/icons/RadioButtonUnchecked";
import {
  getUserByIdRequest,
  toggleEmailUpdateRequest,
} from "../../UsersApiActions";
import ChangeTimezoneDialog from "../../../../utilities/components/TimezoneDialog/TimezoneDialog";
import ChangePasswordDialog from "../../../Auth/components/ChangePasswordDialog/ChangePasswordDialog";
import { useStyles } from "./styles";
import { getUserInfo } from "../../../../utilities/storage";
import defaultUser from "../../../../assets/user.png";
import ImageLoader from "react-load-image";
import Preloader from "../../../../utilities/components/Preloader/Preloader";
import ClinicTiming from "../../../Organizations/components/OrganisationProfile/ClinicTiming";
import { ROLES } from "../../../../utilities/constants";

/**
 * function to render doctor profile
 * @param history: history from router
 * @param getUserById: function to get user by id
 * @param user: user details
 * @param toggleEmailUpdate: fuction to toggle email updates setting
 * @returns {JSX.Element}
 * @constructor
 */
const UserProfile = ({ history, getUserById, user, toggleEmailUpdate }) => {
  const [openTimezoneDialog, setOpenTimezoneDialog] = useState(false);
  const [openChangePasswordDialog, setOpenChangePasswordDialog] =
    useState(false);
  const classes = useStyles();

  useEffect(() => {
    getUserById(getUserInfo().id).then((res) => {});
  }, [getUserById]);

  /**
   * function to handle email update setting
   */
  const handleEmailUpdates = () => {
    const data = {
      enabled: !user.allUpdates,
    };

    toggleEmailUpdate(getUserInfo().id, data).then((res) => {});
  };

  return (
    <>
      {user && (
        <Box className={classes.container}>
          <Box
            className={classes.userProfileContentContainer}
            display="flex"
            flexWrap="wrap"
          >
            {/* LEFT SIDE - Profile Info */}
            <Box display="flex" flex={1} minWidth="300px">
              <Box>
                <ImageLoader src={user.imageUrl ? user.imageUrl : defaultUser}>
                  <img alt={user.id} className={classes.userProfileImage} />
                  <div className={classes.userProfileImageError}>{user.id}</div>
                  <Preloader className={classes.userProfileImage} />
                </ImageLoader>
              </Box>

              <Box className={classes.userProfileTextContainer}>
                <Typography classes={{ root: classes.userProfileName }}>
                  {user.firstName} {user.lastName}
                </Typography>
                <Typography classes={{ root: classes.userProfileEmail }}>
                  {user.email}
                </Typography>
                <Typography classes={{ root: classes.userProfilePhone }}>
                  {user.phone}
                </Typography>

                <Box className={classes.userProfileRoleContainer}>
                  <CircleIcon className={classes.userProfileRoleIcon} />
                  <Typography classes={{ root: classes.userProfileRole }}>
                    {user.Roles
                      ? user.Roles[0].name === "Org Admin"
                        ? "Location Admin"
                        : user.Roles[0].name
                      : ""}
                  </Typography>
                </Box>

                {user.Roles[0].name === "Doctor" && (
                  <>
                    <Typography classes={{ root: classes.userProfilePhone }}>
                      {`Appointment Charges (Online): ${
                        user.consultationFee ?? "-"
                      }`}
                    </Typography>
                    <Typography classes={{ root: classes.userProfilePhone }}>
                      {`Appointment Charges (Offline): ${
                        user.consultationFeeOffline ?? "-"
                      }`}
                    </Typography>
                  </>
                )}

                <FormControlLabel
                  control={
                    <Switch
                      checked={user.allUpdates}
                      onChange={handleEmailUpdates}
                    />
                  }
                  label="Email Alert"
                />
              </Box>
            </Box>

            {/* RIGHT SIDE - Qualifications */}
            {user.Roles[0].name === "Doctor" &&
              user.DoctorQualifications?.length > 0 && (
                <Box flex={1} pl={4} minWidth="300px">
                  <Typography
                    // variant="h6"
                    gutterBottom
                    s
                    className={classes.userProfileName}
                  >
                    Qualifications
                  </Typography>
                  {user.DoctorQualifications.map((qualification, index) => (
                    <Box key={index} mb={1}>
                      <Typography className={classes.profileText}>
                        <strong>Certificate:</strong>{" "}
                        {qualification.certificateName}
                      </Typography>
                      <Typography className={classes.profileText}>
                        <strong>License No.:</strong>{" "}
                        {qualification.licenseNumber}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

            {/* BELOW BOTH - Buttons */}
            <Box
              className={classes.userProfileActionButtonContainer}
              mt={2}
              width="100%"
            >
              <Button
                variant="contained"
                color="primary"
                disableElevation
                startIcon={<EditIcon />}
                classes={{ root: classes.userProfileButton }}
                component={Link}
                to={`/profile/edit`}
              >
                Edit Details
              </Button>

              <Button
                variant="contained"
                color="primary"
                disableElevation
                startIcon={<PasswordIcon />}
                onClick={() => setOpenChangePasswordDialog(true)}
                classes={{ root: classes.userProfileButton }}
              >
                Change Password
              </Button>

              <Button
                variant="contained"
                color="primary"
                disableElevation
                startIcon={<LanguageIcon />}
                onClick={() => setOpenTimezoneDialog(true)}
                classes={{ root: classes.userProfileButton }}
              >
                Change Timezone
              </Button>
            </Box>
          </Box>

          {user?.Roles[0]?.name === ROLES.ROLE_DOCTOR && (
            <Box
              component={Paper}
              className={classes.profileContainer}
              sx={{ mt: 2 }}
            >
              <Box className={classes.profileContentContainer}>
                <ClinicTiming doctorId={getUserInfo().id} mode="doctor" />
              </Box>
            </Box>
          )}
        </Box>
      )}

      <ChangeTimezoneDialog
        openTimezoneDialog={openTimezoneDialog}
        setOpenTimezoneDialog={setOpenTimezoneDialog}
      />

      <ChangePasswordDialog
        openChangePasswordDialog={openChangePasswordDialog}
        setOpenChangePasswordDialog={setOpenChangePasswordDialog}
        history={history}
      />
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.ayucareUsers.user,
  };
};

const mapDispatchToProp = (dispatch) => {
  return {
    getUserById: (id) => {
      return dispatch(getUserByIdRequest(id)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    toggleEmailUpdate: (id, data) => {
      return dispatch(toggleEmailUpdateRequest(id, data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProp)(UserProfile);
